import { DataListValueOverride } from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import { DataListOption } from "src/api/sure-api/dataList/useGetAllDataList";
import {
    Property,
    SsqSsqHeading,
    SsqVersion,
    SsqSsqHeadingSsqProperty,
} from "src/api/sure-api/ssq/useGetOneSsqVersion";

export const isPropertyHidden = (
    propertyId: number,
    hiddenElements: string[],
    ssqVersionStructureId?: number | null,
): boolean => {
    if (hiddenElements.includes(`property-${propertyId}`)) return true;

    return ssqVersionStructureId != null && hiddenElements.includes(`property-${propertyId}_${ssqVersionStructureId}`);
};

export const getVisibleHeadings = (ssqVersion?: SsqVersion, hiddenElements?: string[]): SsqSsqHeading[] => {
    if (ssqVersion === undefined) return [];

    let visibleSsqSsqHeadings = ssqVersion.parentSsqSsqHeadings
        .filter((ssqSsqHeading: SsqSsqHeading) => !ssqSsqHeading.hidden)
        .sort(
            (ssqSsqHeadingA: SsqSsqHeading, ssqSsqHeadingB: SsqSsqHeading) =>
                ssqSsqHeadingA.position - ssqSsqHeadingB.position,
        );

    if (hiddenElements !== undefined) {
        visibleSsqSsqHeadings = filterHeadingsByHiddenElements(visibleSsqSsqHeadings, hiddenElements);
    }

    return visibleSsqSsqHeadings;
};

export const filterHeadingsByHiddenElements = (
    ssqSsqHeadings: SsqSsqHeading[],
    hiddenElements: string[],
): SsqSsqHeading[] => {
    return ssqSsqHeadings.filter(
        (ssqSsqHeading: SsqSsqHeading) => !hiddenElements.includes(`heading-${ssqSsqHeading.id}`),
    );
};

export const getVisibleSsqSsqHeadingSsqProperties = (
    ssqSsqHeading: SsqSsqHeading,
    hiddenElements: string[],
): SsqSsqHeadingSsqProperty[] => {
    const ssqSsqHeadingSsqProperties = ssqSsqHeading.ssqSsqHeadingSsqProperties;

    const propertyIdToHiddenMap = ssqSsqHeadingSsqProperties.reduce(
        (map: { [key: number]: boolean | undefined }, ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty) => {
            map[ssqSsqHeadingSsqProperty.ssqProperty.id] = ssqSsqHeadingSsqProperty.hidden;
            return map;
        },
        {} as { [key: number]: boolean | undefined },
    );
    return ssqSsqHeading.ssqSsqHeadingSsqProperties
        .filter(
            (property: SsqSsqHeadingSsqProperty) =>
                !propertyIdToHiddenMap[property.ssqProperty.id] &&
                !isPropertyHidden(property.ssqProperty.id, hiddenElements, ssqSsqHeading.ssqVersionStructureId),
        )
        .sort(
            (propertyA: SsqSsqHeadingSsqProperty, propertyB: SsqSsqHeadingSsqProperty) =>
                (propertyA.position ?? 0) - (propertyB.position ?? 0),
        );
};

export const extractDataListVersionIdsFromSsqVersion = (
    ssqVersion?: SsqVersion,
    hiddenElements?: string[],
): number[] => {
    const output: Record<number, NonNullable<Property["dataList"]>> = {};

    if (ssqVersion === undefined) return [];

    const normalizedHiddenElements = hiddenElements ?? [];

    const extractFromHeading = (ssqSsqHeading: SsqSsqHeading): void => {
        const ssqSsqHeadingSsqProperties = getVisibleSsqSsqHeadingSsqProperties(
            ssqSsqHeading,
            normalizedHiddenElements,
        );

        for (let ssqSsqHeadingSsqProperty of ssqSsqHeadingSsqProperties) {
            const property = ssqSsqHeadingSsqProperty.ssqProperty;

            for (let subproperty of property.children.filter(
                (subproperty) =>
                    !subproperty.hidden &&
                    !isPropertyHidden(subproperty.id, normalizedHiddenElements, ssqSsqHeading.ssqVersionStructureId),
            )) {
                const dataListVersion = subproperty.dataList;
                if (dataListVersion === undefined) continue;

                output[dataListVersion.id] = dataListVersion;
            }

            const dataListVersion = property.dataList;
            if (dataListVersion === undefined) continue;

            output[dataListVersion.id] = dataListVersion;
        }

        const visibleSubheadings = filterHeadingsByHiddenElements(ssqSsqHeading.children, normalizedHiddenElements);
        visibleSubheadings.forEach(extractFromHeading);
    };

    const visibleSsqSsqHeadings = getVisibleHeadings(ssqVersion, normalizedHiddenElements);
    for (let ssqSsqHeading of visibleSsqSsqHeadings) {
        extractFromHeading(ssqSsqHeading);
    }

    return Object.keys(output).map((id) => Number(id));
};

export const getDataListOptionsWithOverrides = (
    dataListVersion?: Property["dataList"],
    overrides?: DataListValueOverride[],
): DataListOption[] => {
    if (dataListVersion === undefined) return [];

    const output = [...dataListVersion.options].sort(
        (optionA: DataListOption, optionB: DataListOption) => optionA.position - optionB.position,
    );

    if (overrides === undefined) return output;

    const maxPosition = Math.max(...output.map((option) => option.position));

    for (let override of overrides) {
        if (override.dataListVersionId !== dataListVersion.id) continue;

        for (let i = 0; i < override.dataListExtraOptions.length; i++) {
            const dataListExtraOption = override.dataListExtraOptions[i];
            const extraOptionIndex = i + 1;

            const extraOption: DataListOption = {
                id: -1 * dataListExtraOption.id,
                code:
                    dataListExtraOption.code.startsWith("extra:") ?
                        dataListExtraOption.code
                    :   `extra:${dataListExtraOption.code}`,
                label: dataListExtraOption.label,
                position: maxPosition + extraOptionIndex,
                value: "",
            };

            output.push(extraOption);
        }

        break;
    }

    return output;
};
